<template>
  <billing-dialog :visible.sync="visible" class="opc-sdk-credit-dialog" show-close width="780px" @close="handleCancel" @back-event="visible = false">
    <h2 class="opc-sdk-credit-dialog__title">{{ $t('billing.video_sdk_uc.credit_rate_dialog.title') }}</h2>
    <zm-skeleton :class="{'opc-sdk-credit-dialog__skeleton': loading}" :loading="loading" :rows="12" animated>
      <p v-if="data.exampleCredits" class="opc-sdk-credit-dialog__subtitle">{{ $t('billing.video_sdk_uc.credit_rate_dialog.subtitle', {
        amount: $money(data.examplePriceInCents),
        currency: currency,
        credit: data.exampleCredits,
        time: exampleSessionMinute,
      }) }}</p>
      <zm-table
        :data="data.planRates"
        class="opc-sdk-credit-dialog__list"
        row-class-name="opc-sdk-credit-dialog__row"
        stripe
      >
        <zm-table-column
          :label="$t('billing.video_sdk_uc.table_column_label.service_plan')"
          width="360"
        >
          <div slot-scope="scope" class="opc-sdk-credit-dialog__row--service-title-wrap">
            <img v-if="getPlanIcon(scope.row.i18nKey)" :src="getPlanIcon(scope.row.i18nKey)" alt="" />
            <span class="opc-sdk-credit-dialog__row--service-title" tabindex="-1">
            {{ $t(scope.row.i18nKey) }}
          </span>
          </div>
        </zm-table-column>
        <zm-table-column
          :label="$t('billing.video_sdk_uc.table_column_label.rate')"
          width="300"
        >
          <template slot-scope="scope">
            <zm-button data-testid="checkout-button-89" v-if="scope.row.redirect"  @click="openLink(getRedirectUrl(scope.row.i18nKey))" type="link">
              {{ getRedirectLabel(scope.row.i18nKey) }}
            </zm-button>
            <span v-else class="opc-sdk-credit-dialog__row--plan" tabindex="-1">
            {{ getRatePrice(scope.row.ratePrice) }}/{{ scope.row.rateUnit }}
          </span>
          </template>
        </zm-table-column>
      </zm-table>

      <div class="opc-sdk-credit-dialog__notes">Notes</div>
      <ol class="opc-sdk-credit-dialog__notes-desc">
        <li>{{ $t("billing.video_sdk_uc.credit_rate_dialog.note1") }}</li>
        <li>{{ $t("billing.video_sdk_uc.credit_rate_dialog.note2") }}</li>
        <li>
          {{ $t("billing.video_sdk_uc.credit_rate_dialog.note3") }}
          <ul class="opc-sdk-credit-dialog__notes-desc opc-sdk-credit-dialog__notes-desc2">
            <li>{{ $t("billing.video_sdk_uc.credit_rate_dialog.note3_1") }}</li>
            <li>{{ $t("billing.video_sdk_uc.credit_rate_dialog.note3_2") }}</li>
          </ul>
        </li>
      </ol>
    </zm-skeleton>
  </billing-dialog>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { getVideoSdkUcCreditRates } from '@/apis';
import { trackingPlanTypes } from '@/common/constants';
import { getCurrency } from '@/utils/currency';
import { addSymbol } from '@/utils/money';
import BillingDialog from '@/components/billing-dialog.vue';

// TODO need config i18n key, when the backend add a new product with bundle plan
const planIconMap = {
  'billing.video_sdk.ldp.minute': {
    icon: require('@/assets/plans/video_sdk/ldp.svg'),
  },
  'billing.video_sdk.cmr.minute': {
    icon: require('@/assets/plans/video_sdk/cmr.svg'),
  },
  'billing.video_sdk.cmr.storage': {
    icon: require('@/assets/plans/video_sdk/cmr.svg'),
  },
  'billing.video_sdk.zvdktt.minute': {
    icon: require('@/assets/plans/video_sdk/zvdktt.svg'),
  },
  'billing.video_sdk.zvdcrcp.minute': {
    icon: require('@/assets/plans/video_sdk/zvdcrcp.svg'),
  },
  'billing.video_sdk.adp.minute': {
    icon: require('@/assets/plans/video_sdk/adp.svg'),
    link: '/zoomconference/rates?plan=video_sdk',
    ctaI18nKey: 'billing.video_sdk_uc.table_column_label.see_rate_cta',
  },
  'billing.video_sdk.zvsdkqss.minute': {
    icon: require('@/assets/plans/video_sdk/zvsdkqss.svg'),
  },
};

export default {
  name: 'CreditRateFeature',
  mixins: [ConfirmComponent],
  components: {
    BillingDialog,
  },
  data() {
    return {
      visible: false,
      loading: true,
      data: {
        exampleCredits: null,
        examplePriceInCents: null,
        exampleSessionMinute: null,
        planRates: [],
      },
    };
  },
  computed: {
    exampleSessionMinute() {
      return _.parseInt(this.data.exampleSessionMinute);
    },
    currency() {
      return getCurrency();
    },
  },
  created() {
    this.getSdkUcFeature();
  },
  methods: {
    handleSure() {
    },
    getPlanIcon(key) {
      // remove all icons - remove html when we don't need flag
      if (window.newBillingConfig.enableVideoSDKCobrowse) return ''
      return planIconMap[key]?.icon ?? '';
    },
    getRedirectLabel(key) {
      return this.$t(planIconMap[key]?.ctaI18nKey ?? '');
    },
    getRedirectUrl(key) {
      return planIconMap[key]?.link ?? '';
    },
    openLink(link){
      window.open(link)
      this.$tracking.buyInteraction(
        'credit rates',
        `${trackingPlanTypes.VIDEO_SDK_UC} - audio conferencing - see rate`
      );
    },
    async getSdkUcFeature() {
      try {
        const data = await getVideoSdkUcCreditRates();
        if (data?.result?.planRates) {
          data.result.planRates = data.result.planRates.filter((c) => c.i18nKey !== 'billing.video_sdk.zvsdkcbwp.minute' || window.newBillingConfig.enableVideoSDKCobrowse)
        }
        if (data.result) this.data = data.result;
      } finally {
        this.loading = false;
      }
    },
    getRatePrice(ratePrice = 0) {
      const price = +parseFloat(ratePrice / 100)?.toFixed(5);

      return addSymbol(price);
    },
    handleCancel() {
      this.$tracking.buyInteraction(
        'product details',
        `credit rates - close`
      );
    }
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.opc-sdk-credit-dialog {
  &__skeleton {
    padding: 30px 0px;
  }

  .zm-dialog {
    min-width: 522px;
    padding: 70px 60px;
    width: fit-content;

    &__header {
      padding-bottom: 0px !important;
    }
  }

  &__title {
    color: var(--neutral-midnight, #00053D);
    text-align: center;
    font-family: $happy-display;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 36px */
  }

  &__subtitle {
    color: var(--light-mode-gray-90, #131619);
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
  }

  &__desc {
    color: var(--day-foreground-foreground-ada, #666487);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%; /* 16.8px */
  }

  &__list {
    padding: 24px 0px;

    &.zm-table {
      .zm-table__header {
        background: red;

        th {
          .cell {
            padding: 4px 16px 4px 8px;

            span {
              color: var(--day-foreground-foreground, #00053D);
              font-family: $happy-display;
              font-size: 20px;
              font-style: normal;
              font-weight: 600;
              line-height: 120%; /* 24px */
            }
          }
        }
      }
    }

    &.zm-table--striped .zm-table__body tr.zm-table__row--striped td {
      background: #f7f7f8;
    }
  }

  &__row {
    &.zm-table__row {
      td {
        padding: 4px 0px !important;
      }
    }

    &--service-title-wrap {
      display: flex;
      align-items: center;

      img {
        margin-right: 4px;
      }
    }

    &--service-title, &--plan {
      color: var(--day-foreground-foreground, #00053D);
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 24px */
    }

    &--service-title {
    }

    &--plan {
    }
  }
  &__notes {
    font-size: 16px;
    font-weight: 500;
    color: #00053d;
    margin-bottom: 4px;

    &-desc {
      padding-left: 24px;
      font-size: 14px;
      line-height: 20px;
      font-family: "Noto Sans SC", Helvetica, Arial;
      color: rgb(102, 100, 135);
      list-style: decimal;
    }
    &-desc2 {
      list-style: disc;
    }
  }
}

@media (min-width: 721px) and (max-width: 1024px) {
  .opc-sdk-credit-dialog > .zm-dialog {
      width: 95% !important;
      margin-top: 6vh !important;
      height: 75vh;
      overflow-y: auto;
  }
}
</style>
